import lodash from 'lodash';
import propTypes from 'prop-types';
import {
    forwardRef,
    memo,
    createElement as rc,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { ThemeContext } from 'styled-components';
import styled from '../../styled';
import sizes from '../../styling/themes/gl2025/sizes';
import { GestureContext } from '../contextProviders/GestureRecognizer';
import View from '../View';
import ListItemRenderer from './ListItemRenderer';
import useListOuterScrollingElement from './useListOuterScrollingElement';
const { memoize } = lodash;

// If you don't have this min-height, then Autosize will freak out and
// not display any content.
// Dropdown needs height 100% or it will only go to the min-height.
const NonTouchContainer = styled(View)`
    min-height: ${sizes.fixedSpacing['9xl']}px;
    height: 100%;
    width: 100%;
    flex-grow: 1;
    position: relative;

    /* GL2025: Scrollbar styling */
    ${({ theme }) =>
        theme.selectedTheme === 'gl2025'
            ? `
    * {
        &::-webkit-scrollbar {
            width: ${sizes.fixedSpacing.md}px;
            height: ${sizes.fixedSpacing.md}px;
        }
        &::-webkit-scrollbar-track {
            margin-top: ${theme.listHeaderHeight}px;
            margin-bottom: ${sizes.fixedSpacing['6xl'] + 2}px;
            background: ${theme.scrollbarTrack};
            border-radius: ${sizes.radius.full}px;
        }
        &::-webkit-scrollbar-thumb {
            background: ${theme.scrollbarThumb};
            border-radius: ${sizes.radius.full}px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: ${theme.scrollbarThumbHover};
        }
        &::-webkit-scrollbar-corner {
            background: transparent;
        }
    }
    `
            : ''}
`;

let StickyHeader = styled(View).attrs({ name: 'sticky-header' })`
    position: sticky;
    left: 0;
    top: 0;
    z-index: ${({ theme }) => theme.zindex.ListStickyHeader};
    overflow: visible;
    background: transparent;
`;

// This is used to render a sticky list header if a header is passed into the component
// as child
const getInnerElementType = (Header, headerHeightPixels) => {
    // The `innerElementType` property is taken by a react-window list to encapsulate the rows.
    // It just uses a div element if you don't pass something in.
    // prettier-ignore
    const innerElementType = forwardRef((props, ref) => {
        const { children, ...rest } = props || {};
        // This encapsulates all list rows (including the header).
        return rc('div', { ref, ...rest },
            // Make the header stick to the top of the list
            rc(StickyHeader, { style: { height: headerHeightPixels } },
                // passed into the List component as a child
                Header
            ),
            // The rest of the rows in the list
            children
        );
    });
    innerElementType.displayName = 'ListInnerElement';
    innerElementType.propTypes = {
        children: propTypes.array
    };
    return innerElementType;
};
function List(props, forwardedRef) {
    const {
        id,
        itemCount,
        data: items,
        Row,
        RowDetail,
        onClick,
        onRenderItem,
        highlightedIndex,
        selectedItem,
        getItemProps,
        itemHeightPixels,
        children,
        className,
        listType,
        blockLeftSwipe = true,
        blockRightSwipe = true,
        onScrollLayoutChange = () => {
            /*default to noop*/
        },
        endOfRecordsText,
        offlineText,
        hideVerticalScrollOverlay = false,
        setIsScrollable
    } = props || {};
    const { suspendLeftSwipe, suspendRightSwipe } = useContext(GestureContext);

    const ref = useRef();
    const internalListRef = useRef();
    const listRef = forwardedRef || internalListRef;

    useLayoutEffect(() => {
        const releases = [];
        if (!ref.current) {
            throw new Error('No reference for the scrolling element is defined.');
        }
        if (blockLeftSwipe) {
            releases.push(suspendLeftSwipe(() => ref.current.getBoundingClientRect()));
        }
        if (blockRightSwipe) {
            releases.push(suspendRightSwipe(() => ref.current.getBoundingClientRect()));
        }
        return () => releases.forEach(release => release());
    }, [blockLeftSwipe, blockRightSwipe, suspendRightSwipe, suspendLeftSwipe]);

    const theme = useContext(ThemeContext);

    // Reset list item sizes when theme changes (row heights differ between themes)
    useEffect(() => {
        if (listRef.current && listRef.current.resetAfterIndex) {
            listRef.current.resetAfterIndex(0);
        }
    }, [listRef, theme.listLineHeight, theme.listHeaderHeight, theme.selectedTheme, offlineText]);

    let innerItemCount = itemCount;
    // The only child that should be passed in is the List's header.
    const Header = children;
    let innerElementType,
        hasHeader = false;
    if (Header) {
        hasHeader = true;
        // Use listHeaderHeight if available (gl2025 theme), otherwise fall back to listLineHeight
        const headerHeight = theme.listHeaderHeight ?? itemHeightPixels ?? theme.listLineHeight;
        innerElementType = getInnerElementType(Header, headerHeight);
        // Add one for the header
        innerItemCount++;
    }
    // Allow for 'End of records' message by adding one more row to the count
    if (endOfRecordsText != null && endOfRecordsText.length > 0) {
        innerItemCount++;
        // Allow for offline message row below 'End of records'
        if (offlineText) {
            innerItemCount++;
        }
    }
    const itemData = useMemo(
        () => ({
            Row,
            RowDetail,
            items,
            selectedItem,
            highlightedIndex,
            theme,
            getItemProps,
            onClick,
            onRenderItem,
            hasHeader,
            listType,
            offlineText
        }),
        [
            Row,
            RowDetail,
            items,
            selectedItem,
            highlightedIndex,
            theme,
            getItemProps,
            onClick,
            onRenderItem,
            hasHeader,
            listType,
            offlineText
        ]
    );
    const outerElementType = useListOuterScrollingElement(onScrollLayoutChange, id, items);
    const VariableHeightList = memoize(
        ({ height, width }) => {
            // ** USEFUL FOR DEBUGGING EXTRA CALLS. **
            //console.info(`NOT MEMOIZED ${height}|${width}|${JSON.stringify(items[0])}`);

            // prettier-ignore
            return rc(VariableSizeList, {
                ref: listRef,
                id: `fixed-list-${id}`,
                name: 'List',
                height,
                width,
                itemCount: innerItemCount,
                itemSize: RowDetail.getItemSize ? RowDetail.getItemSize : (index) => {
                    // Use listHeaderHeight for header row (index 0) if available, otherwise use listLineHeight
                    if (index === 0 && theme.listHeaderHeight) {
                        return theme.listHeaderHeight;
                    }
                    return itemHeightPixels ?? theme.listLineHeight;
                },
                // Fixed Size List expects 'itemData' to include any data needed by the item renderer
                itemData,
                innerElementType,
                outerElementType,
                className
            },
                ListItemRenderer
            );
        },
        ({ height, width }) => `${height}|${width}|${theme.listLineHeight}|${theme.listHeaderHeight}`
    );

    // prettier-ignore
    const listResult = rc(AutoSizer, { name: 'auto-sizer', nonce: window.__webpack_nonce__ }, VariableHeightList);

    // GL2025: Scrollbar fade overlay - only show when scrollbar is visible
    const [isVerticalScrollable, setIsVerticalScrollable] = useState(false);
    const [isHorizontalScrollable, setIsHorizontalScrollable] = useState(false);
    const isGl2025 = theme?.selectedTheme === 'gl2025';

    useEffect(() => {
        if (!isGl2025 || !ref.current) return;

        let resizeObserver;
        let scrollEl;

        const checkScrollable = () => {
            if (!scrollEl) {
                scrollEl = ref.current?.querySelector('[id^="outerElementForList"]');
            }
            if (scrollEl) {
                setIsVerticalScrollable(scrollEl.scrollHeight > scrollEl.clientHeight);
                setIsHorizontalScrollable(scrollEl.scrollWidth > scrollEl.clientWidth);

                // set isScrollable for dropdown lists
                setIsScrollable?.(scrollEl.scrollHeight > scrollEl.clientHeight);
            }
        };

        // Delay initial check to let DOM settle
        const timeoutId = setTimeout(() => {
            scrollEl = ref.current?.querySelector('[id^="outerElementForList"]');
            if (scrollEl) {
                checkScrollable();
                resizeObserver = new ResizeObserver(checkScrollable);
                resizeObserver.observe(scrollEl);
                // Also check on scroll in case content loads dynamically
                scrollEl.addEventListener('scroll', checkScrollable);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver?.disconnect();
            scrollEl?.removeEventListener('scroll', checkScrollable);
        };
    }, [isGl2025, items.length, itemCount, setIsScrollable]);

    // It is incredibly difficult to get the auto-sizer to work correctly in unit testing
    // and the list will not render any rows unless the autosizer says there is room.
    // So this makes sure that the autosizer makes room to render rows during unit tests.

    if (__UNIT_TESTING__) {
        // prettier-ignore
        return rc(View, {
            ref,
            id: `auto-sizer-container-${id}`,
            'data-testid': `auto-sizer-container-${id}`,
            className,
            style: {
                height: '500px',
                width: '500px',
                flexGrow: 1
            }
        },
            listResult
        );
    }

    // Calculate overlay dimensions
    const headerHeight = theme?.listHeaderHeight ?? 34;
    // scrollbar width + "End of records" span height
    const verticalOffset = headerHeight + sizes.fixedSpacing.md + theme.listLineHeight;

    // Vertical scrollbar fade overlay (right side) - skip if hideVerticalScrollOverlay is true
    const verticalOverlayStyle =
        isGl2025 && isVerticalScrollable && !hideVerticalScrollOverlay
            ? {
                  position: 'absolute',
                  top: Header ? `${headerHeight}px` : '0',
                  right: `${sizes.fixedSpacing.md}px`,
                  width: `${headerHeight}px`,
                  height: Header ? `calc(100% - ${verticalOffset}px)` : '100%',
                  background: theme?.listScrollbarOverlay?.vertical,
                  pointerEvents: 'none',
                  zIndex: 5
              }
            : null;

    // Horizontal scrollbar fade overlay (bottom)
    const horizontalOverlayStyle =
        isGl2025 && isHorizontalScrollable
            ? {
                  position: 'absolute',
                  bottom: `${sizes.fixedSpacing.md}px`,
                  left: '0',
                  width: `calc(100% - ${isVerticalScrollable ? sizes.fixedSpacing.md : 0}px)`, // Account for vertical scrollbar if present
                  height: `${headerHeight}px`,
                  background: theme?.listScrollbarOverlay?.horizontal,
                  pointerEvents: 'none',
                  zIndex: 5
              }
            : null;

    // prettier-ignore
    return rc(NonTouchContainer, { ref, id: 'non-touch-container' },
        listResult,
        verticalOverlayStyle && rc('div', { style: verticalOverlayStyle }),
        horizontalOverlayStyle && rc('div', { style: horizontalOverlayStyle })
    );

    // return listResult;
}

List.propTypes = {
    // Only one header element allowed.
    children: propTypes.element
};
const _List = memo(forwardRef(List));
_List.displayName = 'MemoizedList';
export default _List;
